import { Box, Card, Divider, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import FormEINWithFileDrop from '../../../FormFields/FormEINWithFileDrop';
import FormFileDrop from '../../../FormFields/FormFileDrop';
import FormNPIWithFileDrop from '../../../FormFields/FormNPIWithFileDrop';
import FormTextField from '../../../FormFields/FormTextField';
import FormTextFieldWithAddMore from '../../../FormFields/FormTextFieldWithAddMore';
import FormTextWithFileDropAndAddMore from '../../../FormFields/FormTextWithFileDropAndAddMore';
import FormTitle from '../../../FormFields/FormTitle';
import FormStaffInfoField from '../../../FormFields/StaffInfoField/FormStaffInfoField';
import { FILE_TYPES } from '../../../formFileTypesEnum';
import { BUSINESS_DEVELOPMENT } from '../../../formQuestionsEnum';
import { formCardStyle } from '../../../TransitionStyles';
import { validatePositiveNumber } from '../../../ValidateFunctions';

GeneralInfoForm.propTypes = {
  permissions: PropTypes.object,
};

export default function GeneralInfoForm({ permissions }) {
  const [focusedField, setFocusedField] = useState(null);

  const fieldIds = [
    BUSINESS_DEVELOPMENT.BED_COUNT.id,
    BUSINESS_DEVELOPMENT.TWELVE_MONTH_LOOK_BACK_FILE.id,
    BUSINESS_DEVELOPMENT.W9_FILE.id,
    BUSINESS_DEVELOPMENT.EIN_NUMBER.id,
    BUSINESS_DEVELOPMENT.NPI_NUMBER.id,
    BUSINESS_DEVELOPMENT.REFERRING_HOSPITALS.id,
    BUSINESS_DEVELOPMENT.MANAGED_CARE_VA_INSURANCE_CONTRACTS.id,
    BUSINESS_DEVELOPMENT.MEDICAL_DIRECTOR_FIRST_NAME.id,
    BUSINESS_DEVELOPMENT.MEDICAL_DIRECTOR_CONTRACT.id,
    BUSINESS_DEVELOPMENT.ADMISSIONS_DIRECTOR.id,
    BUSINESS_DEVELOPMENT.HOME_HEALTH_AGENCIES.id,
    BUSINESS_DEVELOPMENT.HOSPICE_AGENCIES.id,
  ];

  const showForm = fieldIds.some((id) => id in permissions);

  return (
    showForm && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          General Information
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid container spacing={1.5}>
          {BUSINESS_DEVELOPMENT.BED_COUNT.id in permissions && (
            <>
              <Grid item xs={12}>
                <FormTitle fieldAttributes={{ questionTitle: 'Number of beds', fieldType: 'title' }} />
              </Grid>
              <Grid item xs={3}>
                <FormTitle fieldAttributes={{ questionTitle: 'SNF' }} />
                <FormTextField
                  fieldName={`${BUSINESS_DEVELOPMENT.BED_COUNT.fieldName}.bedCountSNF`}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                  fieldAttributes={{
                    isEditable: permissions[BUSINESS_DEVELOPMENT.BED_COUNT.id],
                    validation: {
                      pattern: { ...validatePositiveNumber },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <FormTitle fieldAttributes={{ questionTitle: 'ILF' }} />
                <FormTextField
                  fieldName={`${BUSINESS_DEVELOPMENT.BED_COUNT.fieldName}.bedCountILF`}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                  fieldAttributes={{
                    isEditable: permissions[BUSINESS_DEVELOPMENT.BED_COUNT.id],
                    validation: {
                      pattern: { ...validatePositiveNumber },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <FormTitle fieldAttributes={{ questionTitle: 'ALF' }} />
                <FormTextField
                  fieldName={`${BUSINESS_DEVELOPMENT.BED_COUNT.fieldName}.bedCountALF`}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                  fieldAttributes={{
                    isEditable: permissions[BUSINESS_DEVELOPMENT.BED_COUNT.id],
                    validation: {
                      pattern: { ...validatePositiveNumber },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <FormTitle fieldAttributes={{ questionTitle: 'PC' }} />
                <FormTextField
                  fieldName={`${BUSINESS_DEVELOPMENT.BED_COUNT.fieldName}.bedCountPC`}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                  fieldAttributes={{
                    isEditable: permissions[BUSINESS_DEVELOPMENT.BED_COUNT.id],
                    validation: {
                      pattern: { ...validatePositiveNumber },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ mb: 2, mt: 2, borderWidth: 1 }} />
              </Grid>
            </>
          )}
          {BUSINESS_DEVELOPMENT.TWELVE_MONTH_LOOK_BACK_FILE.id in permissions && (
            <Grid item xs={6}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle fieldAttributes={{ questionTitle: '12 month look back', fieldType: 'file' }} />
                <FormFileDrop
                  fieldName={BUSINESS_DEVELOPMENT.TWELVE_MONTH_LOOK_BACK_FILE.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[BUSINESS_DEVELOPMENT.TWELVE_MONTH_LOOK_BACK_FILE.id],
                    fileTypeId: FILE_TYPES.TWELVE_MONTH_LOOKBACK,
                  }}
                />
              </Box>
            </Grid>
          )}
          {BUSINESS_DEVELOPMENT.W9_FILE.id in permissions && (
            <Grid item xs={6}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle fieldAttributes={{ questionTitle: 'W-9', fieldType: 'file' }} />
                <FormFileDrop
                  fieldName={BUSINESS_DEVELOPMENT.W9_FILE.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[BUSINESS_DEVELOPMENT.W9_FILE.id],
                    fileTypeId: FILE_TYPES.W9,
                  }}
                />
              </Box>
            </Grid>
          )}
          {BUSINESS_DEVELOPMENT.EIN_NUMBER.id in permissions && (
            <Grid item xs={6}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle fieldAttributes={{ questionTitle: 'EIN Number', fieldType: 'file' }} />
                <FormEINWithFileDrop
                  fieldName={BUSINESS_DEVELOPMENT.EIN_NUMBER.fieldName}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                  fieldAttributes={{
                    isEditable: permissions[BUSINESS_DEVELOPMENT.EIN_NUMBER.id],
                    fileTypeId: FILE_TYPES.BUYER_EIN_NUMBER,
                  }}
                />
              </Box>
            </Grid>
          )}
          {BUSINESS_DEVELOPMENT.NPI_NUMBER.id in permissions && (
            <Grid item xs={6}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <FormTitle fieldAttributes={{ questionTitle: 'NPI Number', fieldType: 'file' }} />
                <FormNPIWithFileDrop
                  fieldName={BUSINESS_DEVELOPMENT.NPI_NUMBER.fieldName}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                  fieldAttributes={{
                    isEditable: permissions[BUSINESS_DEVELOPMENT.NPI_NUMBER.id],
                    fileTypeId: FILE_TYPES.BUYER_NPI_NUMBER,
                  }}
                />
              </Box>
            </Grid>
          )}
          {[
            BUSINESS_DEVELOPMENT.TWELVE_MONTH_LOOK_BACK_FILE.id,
            BUSINESS_DEVELOPMENT.W9_FILE.id,
            BUSINESS_DEVELOPMENT.EIN_NUMBER.id,
            BUSINESS_DEVELOPMENT.NPI_NUMBER.id,
          ].some((id) => id in permissions) && (
            <Grid item xs={12}>
              <Divider sx={{ mb: 2, mt: 2, borderWidth: 1 }} />
            </Grid>
          )}
          {BUSINESS_DEVELOPMENT.REFERRING_HOSPITALS.id in permissions && (
            <>
              <Grid item xs={12}>
                <FormTitle fieldAttributes={{ questionTitle: 'Referring hospitals' }} />
                <FormTextFieldWithAddMore
                  fieldName={BUSINESS_DEVELOPMENT.REFERRING_HOSPITALS.fieldName}
                  isEditable={permissions[BUSINESS_DEVELOPMENT.REFERRING_HOSPITALS.id]}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ mb: 2, mt: 2, borderWidth: 1 }} />
              </Grid>
            </>
          )}
          {BUSINESS_DEVELOPMENT.MANAGED_CARE_VA_INSURANCE_CONTRACTS.id in permissions && (
            <>
              <Grid item xs={12}>
                <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                  <FormTitle
                    fieldAttributes={{ questionTitle: 'Managed care, VA, insurance contracts', fieldType: 'file' }}
                  />
                  <FormFileDrop
                    fieldName={BUSINESS_DEVELOPMENT.MANAGED_CARE_VA_INSURANCE_CONTRACTS.fieldName}
                    fieldAttributes={{
                      isEditable: permissions[BUSINESS_DEVELOPMENT.MANAGED_CARE_VA_INSURANCE_CONTRACTS.id],
                      fileTypeId: FILE_TYPES.MANAGED_CARE_VA_INSURANCE_CONTRACT,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ mb: 2, mt: 2, borderWidth: 1 }} />
              </Grid>
            </>
          )}
          {[
            BUSINESS_DEVELOPMENT.MEDICAL_DIRECTOR_FIRST_NAME.id,
            BUSINESS_DEVELOPMENT.MEDICAL_DIRECTOR_CONTRACT.id,
          ].some((id) => id in permissions) && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="text.secondary">
                  Medical director
                </Typography>
              </Grid>
              {BUSINESS_DEVELOPMENT.MEDICAL_DIRECTOR_FIRST_NAME.id in permissions && (
                <Grid item xs={12}>
                  <FormTitle fieldAttributes={{ questionTitle: 'First name' }} />
                  <FormTextField
                    fieldName={BUSINESS_DEVELOPMENT.MEDICAL_DIRECTOR_FIRST_NAME.fieldName}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                    fieldAttributes={{
                      isEditable: permissions[BUSINESS_DEVELOPMENT.MEDICAL_DIRECTOR_FIRST_NAME.id],
                    }}
                  />
                </Grid>
              )}
              {BUSINESS_DEVELOPMENT.MEDICAL_DIRECTOR_CONTRACT.id in permissions && (
                <Grid item xs={12}>
                  <FormTitle fieldAttributes={{ questionTitle: 'Contract', fieldType: 'file' }} />
                  <FormFileDrop
                    fieldName={BUSINESS_DEVELOPMENT.MEDICAL_DIRECTOR_CONTRACT.fieldName}
                    fieldAttributes={{
                      isEditable: permissions[BUSINESS_DEVELOPMENT.MEDICAL_DIRECTOR_CONTRACT.id],
                      fileTypeId: FILE_TYPES.MEDICAL_DIRECTOR_CONTRACT,
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider sx={{ mb: 2, mt: 2, borderWidth: 1 }} />
              </Grid>
            </>
          )}
          {BUSINESS_DEVELOPMENT.ADMISSIONS_DIRECTOR.id in permissions && (
            <>
              <Grid item xs={12}>
                <FormStaffInfoField
                  fieldName={BUSINESS_DEVELOPMENT.ADMISSIONS_DIRECTOR.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[BUSINESS_DEVELOPMENT.ADMISSIONS_DIRECTOR.id],
                    questionTitle: 'Admissions director',
                    fieldName: BUSINESS_DEVELOPMENT.ADMISSIONS_DIRECTOR.fieldName,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ mb: 2, mt: 2, borderWidth: 1 }} />
              </Grid>
            </>
          )}
          {BUSINESS_DEVELOPMENT.HOME_HEALTH_AGENCIES.id in permissions && (
            <>
              <Grid item xs={12}>
                <FormTitle fieldAttributes={{ questionTitle: 'Contracted home health agencies' }} />
                <FormTextFieldWithAddMore
                  fieldName={BUSINESS_DEVELOPMENT.HOME_HEALTH_AGENCIES.fieldName}
                  isEditable={permissions[BUSINESS_DEVELOPMENT.HOME_HEALTH_AGENCIES.id]}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ mb: 2, mt: 2, borderWidth: 1 }} />
              </Grid>
            </>
          )}
          {BUSINESS_DEVELOPMENT.HOSPICE_AGENCIES.id in permissions && (
            <>
              <Grid item xs={12}>
                <FormTitle
                  fieldAttributes={{
                    questionTitle: 'List of contracted hospice agencies',
                    fieldType: 'title',
                  }}
                />
                <FormTextWithFileDropAndAddMore
                  fieldName="hospiceAgencies"
                  requireNameWhenHasFiles
                  fieldAttributes={{
                    isEditable: permissions[BUSINESS_DEVELOPMENT.HOSPICE_AGENCIES.id],
                    fileTypeId: FILE_TYPES.HOSPICE_AGENCY_CONTRACT,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ mb: 2, mt: 2, borderWidth: 1 }} />
              </Grid>
            </>
          )}
        </Grid>
      </Card>
    )
  );
}
